import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { parseISO } from 'date-fns';
import { aiAssistantService } from '@services';
import { translateApiError } from '@helpers/api';
import { AI_TASK_TYPE, LocationSuggestionType } from '@constants/ai-assistant';
import { DEFAULT_HOURS_INTERVAL, MAX_ADDITIONAL_CATEGORIES, MAX_SERVICES } from '@constants/companies';
import { AI_KIND, DESCRIPTION_FIELDS, aiKindFor, descriptionTaskFor } from './recommendations';
import { useLiveScoreValue } from './LiveScoreProvider';
import {
    PLACE_FIELDS,
    buildLocationDraft,
    filledServices,
    placeKey,
    placeOf,
    unlistedHolidays,
    unlistedServices,
    unselectedCategories,
} from './locationDraft';

export const ASSIST_STATUS = {
    LOADING: 'loading',
    READY: 'ready',
    EMPTY: 'empty',
    ERROR: 'error',
};

const SUGGESTION_TYPE = {
    [AI_KIND.SERVICES]: LocationSuggestionType.Services,
    [AI_KIND.CATEGORIES]: LocationSuggestionType.Categories,
    [AI_KIND.SPECIAL_HOURS]: LocationSuggestionType.SpecialHours,
};

const DIRTY = { shouldDirty: true, shouldValidate: true };

const omit = (object, key) => Object.fromEntries(Object.entries(object).filter(([name]) => name !== key));

const pick = (object, keys) => Object.fromEntries(Object.entries(object).filter(([name]) => keys.has(name)));

const ScoreAssistantContext = createContext(null);
const AiDraftedFieldsContext = createContext([]);

const filledSecondaryCategories = formValues =>
    (formValues.secondaryCategories || []).filter(row => row?.category?.[0]?.id);

const freeSlots = (kind, formValues) => {
    if (kind === AI_KIND.SERVICES) return Math.max(MAX_SERVICES - filledServices(formValues).length, 0);
    if (kind === AI_KIND.CATEGORIES) {
        const primarySlot = formValues.primaryCategory?.length ? 0 : 1;
        return Math.max(MAX_ADDITIONAL_CATEGORIES - filledSecondaryCategories(formValues).length + primarySlot, 0);
    }
    return Infinity;
};

const suggestedItems = (kind, result) => {
    if (kind === AI_KIND.SERVICES) return result?.services || [];
    if (kind === AI_KIND.CATEGORIES) {
        return (result?.categories || []).map(({ id, displayName }) => ({ id, displayName }));
    }
    return result?.specialHours || [];
};

const notYetInForm = (kind, items, formValues) => {
    if (kind === AI_KIND.SERVICES) return unlistedServices(items, formValues);
    if (kind === AI_KIND.CATEGORIES) return unselectedCategories(items, formValues);
    return unlistedHolidays(items, formValues);
};

const currentSelection = (kind, entry, formValues) => {
    const items = notYetInForm(kind, entry.items, formValues);
    const limit = freeSlots(kind, formValues);
    const chosen = entry.selected.filter(item => items.includes(item)).slice(0, limit);
    return { items, chosen, limit };
};

export const ScoreAssistantProvider = ({ companyId, sessionKey, onReveal, onGeneratingChange, children }) => {
    const { control, getValues, setValue } = useFormContext();
    const { isDirty, isSubmitting } = useFormState({ control });
    const descriptionValues = useWatch({ control, name: DESCRIPTION_FIELDS });
    const place = placeKey(useWatch({ control, name: PLACE_FIELDS }));

    const { recommendations } = useLiveScoreValue();
    const recommendedKeys = recommendations
        .filter(recommendation => !recommendation.withoutAi)
        .map(recommendation => recommendation.key)
        .sort();
    const recommendedRef = useRef(new Set());
    recommendedRef.current = new Set(recommendedKeys);

    const [assist, setAssist] = useState({});
    const [drafted, setDrafted] = useState({});
    const generationRef = useRef(0);

    useEffect(() => {
        if (!isDirty) setDrafted({});
    }, [isDirty, sessionKey]);

    useEffect(() => {
        generationRef.current += 1;
        setAssist({});
    }, [sessionKey]);

    useEffect(() => {
        setAssist(current => omit(current, AI_KIND.SPECIAL_HOURS));
    }, [place]);

    useEffect(() => {
        setAssist(current =>
            Object.keys(current).every(key => recommendedRef.current.has(key))
                ? current
                : pick(current, recommendedRef.current),
        );
    }, [recommendedKeys.join(',')]);

    const isGenerating = Object.values(assist).some(entry => entry.status === ASSIST_STATUS.LOADING);

    useEffect(() => {
        onGeneratingChange?.(isGenerating);
    }, [isGenerating]);

    const setAssistFor = useCallback(
        (key, value) => setAssist(current => (value ? { ...current, [key]: value } : omit(current, key))),
        [],
    );

    const draftedFieldsKey = DESCRIPTION_FIELDS.filter(
        (field, index) => drafted[field] && drafted[field].draft === descriptionValues?.[index],
    ).join(',');
    const draftedFields = useMemo(() => (draftedFieldsKey ? draftedFieldsKey.split(',') : []), [draftedFieldsKey]);

    const applyDescription = (field, draft) => {
        const value = getValues(field) || '';
        setDrafted(current => {
            const previous = current[field]?.draft === value ? current[field].previous : value;
            return { ...current, [field]: { previous, draft } };
        });
        setValue(field, draft, DIRTY);
    };

    const requestSuggestions = async (key, kind, isCurrent) => {
        const result = await aiAssistantService.getLocationSuggestions({
            type: SUGGESTION_TYPE[kind],
            locationId: companyId,
            draft: buildLocationDraft(getValues()),
        });
        if (!isCurrent()) return;
        const formValues = getValues();
        const items = notYetInForm(kind, suggestedItems(kind, result), formValues);
        const selected = kind === AI_KIND.SPECIAL_HOURS ? [] : items.slice(0, freeSlots(kind, formValues));
        setAssistFor(
            key,
            items.length ? { status: ASSIST_STATUS.READY, items, selected } : { status: ASSIST_STATUS.EMPTY },
        );
    };

    const requestDescription = async (field, isCurrent) => {
        const formValues = getValues();
        const response = await aiAssistantService.getContextualTask({
            input: formValues[field] || '',
            type: descriptionTaskFor(field),
            taskType: AI_TASK_TYPE.CONTEXTUAL,
            locationId: companyId,
            locationDraft: buildLocationDraft(formValues, field),
        });
        if (!isCurrent()) return;
        const draft = response?.result?.trim() || '';
        if (!draft) {
            setAssistFor(field, { status: ASSIST_STATUS.EMPTY });
        } else if (getValues(field)?.trim()) {
            setAssistFor(field, { status: ASSIST_STATUS.READY, draft });
        } else {
            applyDescription(field, draft);
            setAssistFor(field, null);
        }
    };

    const request = async key => {
        const generation = generationRef.current;
        const kind = aiKindFor(key);
        const isCurrent = () =>
            generation === generationRef.current &&
            recommendedRef.current.has(key) &&
            (kind !== AI_KIND.SPECIAL_HOURS || placeOf(getValues()) === place);
        setAssistFor(key, { status: ASSIST_STATUS.LOADING });
        try {
            if (kind === AI_KIND.DESCRIPTION) {
                await requestDescription(key, isCurrent);
            } else {
                await requestSuggestions(key, kind, isCurrent);
            }
        } catch (error) {
            if (isCurrent()) {
                setAssistFor(key, { status: ASSIST_STATUS.ERROR, message: translateApiError(error?.errorCode) });
            }
        }
    };

    const suggestionView = key => currentSelection(aiKindFor(key), assist[key], getValues());

    const toggle = (key, item) => {
        const formValues = getValues();
        setAssist(current => {
            const entry = current[key];
            if (!entry?.selected) return current;
            const { chosen, limit } = currentSelection(aiKindFor(key), entry, formValues);
            const isChosen = chosen.includes(item);
            if (!isChosen && chosen.length >= limit) return current;
            const selected = isChosen ? chosen.filter(other => other !== item) : [...chosen, item];
            return { ...current, [key]: { ...entry, selected } };
        });
    };

    const addToForm = (kind, chosen, formValues) => {
        switch (kind) {
            case AI_KIND.SERVICES:
                setValue(
                    'services',
                    [...filledServices(formValues), ...chosen].map(name => ({ name })),
                    DIRTY,
                );
                break;
            case AI_KIND.CATEGORIES: {
                let picks = chosen;
                if (!formValues.primaryCategory?.length && picks.length) {
                    setValue('primaryCategory', [picks[0]], DIRTY);
                    picks = picks.slice(1);
                }
                setValue(
                    'secondaryCategories',
                    [...filledSecondaryCategories(formValues), ...picks.map(category => ({ category: [category] }))],
                    DIRTY,
                );
                break;
            }
            default:
                setValue(
                    'specialHours',
                    [
                        ...(formValues.specialHours || []),
                        ...chosen.map(({ date }) => ({
                            date: parseISO(date),
                            isClosed: true,
                            hours: [{ ...DEFAULT_HOURS_INTERVAL }],
                        })),
                    ],
                    DIRTY,
                );
        }
    };

    const apply = key => {
        const entry = assist[key];
        if (!entry) return;
        const kind = aiKindFor(key);

        if (kind === AI_KIND.DESCRIPTION) {
            applyDescription(key, entry.draft);
        } else {
            const formValues = getValues();
            addToForm(kind, currentSelection(kind, entry, formValues).chosen, formValues);
        }

        setAssistFor(key, null);
    };

    const undo = field => {
        const entry = drafted[field];
        if (!entry) return;
        setValue(field, entry.previous, DIRTY);
        setDrafted(current => omit(current, field));
    };

    const value = {
        assist,
        draftedFields,
        isLocked: !!isSubmitting,
        request,
        toggle,
        apply,
        undo,
        dismiss: key => setAssistFor(key, null),
        reveal: onReveal,
        suggestionView,
    };

    return (
        <ScoreAssistantContext.Provider value={value}>
            <AiDraftedFieldsContext.Provider value={draftedFields}>{children}</AiDraftedFieldsContext.Provider>
        </ScoreAssistantContext.Provider>
    );
};

export const useScoreAssistant = () => useContext(ScoreAssistantContext);

export const useAiDraftedFields = () => useContext(AiDraftedFieldsContext);
